import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { fetchInstitutions } from "../lib/api";
import { ArrowUpRight, ChevronRight, MapPin, Layers } from "lucide-react";
import { slugify } from "./Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Breadcrumb + H1 + intro for category / state landing pages.
 */
export const LandingIntro = ({ mode, value, total }) => {
  // mode = "category" | "state"
  const label = mode === "category" ? "Category" : "State";
  const heading =
    mode === "category"
      ? `${value} — Contact directory`
      : `Banks & NBFCs in ${value}`;
  const subline =
    mode === "category"
      ? `Every RBI-registered ${value} with Nodal, Grievance, Recovery and Legal Head contacts indexed on BankDesks.com.`
      : `Every bank and NBFC headquartered in ${value} — escalation contacts for grievance, recovery and legal matters, compiled from RBI disclosures.`;

  return (
    <section
      className="border-b border-[#0A0A0A] bg-white"
      data-testid="landing-intro"
      aria-label={`${label} landing`}
    >
      <nav className="bg-[#F8F9FA] border-b border-[#E5E7EB]" aria-label="Breadcrumb">
        <ol className="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12 py-3 flex items-center gap-2 text-xs font-mono text-[#4B5563]">
          <li><Link to="/" className="hover:text-[#002FA7]">Home</Link></li>
          <li><ChevronRight className="w-3 h-3" /></li>
          <li className="uppercase tracking-[0.2em]">{label}</li>
          <li><ChevronRight className="w-3 h-3" /></li>
          <li className="text-[#0A0A0A] font-bold truncate">{value}</li>
        </ol>
      </nav>
      <div className="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12 py-12">
        <div className="flex items-center gap-3 mb-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">
            {label} landing
          </span>
          {total != null && (
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] bg-[#0A0A0A] text-white px-2 py-0.5">
              {total.toLocaleString()} institutions
            </span>
          )}
        </div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[1.02] max-w-3xl">
          {heading}
        </h1>
        <p className="mt-5 max-w-2xl text-base text-[#4B5563] leading-relaxed">
          {subline}
        </p>
      </div>
    </section>
  );
};

/**
 * Related block shown below the directory on category/state pages.
 * Shows (a) other categories or states to explore, (b) top verified entries
 * within the current scope.
 */
export const LandingRelated = ({ mode, value }) => {
  const [meta, setMeta] = useState(null);
  const [top, setTop] = useState([]);

  useEffect(() => {
    axios.get(`${API}/meta`).then((r) => setMeta(r.data)).catch(() => {});
    const filters =
      mode === "category"
        ? { category: value, detailed: "verified", page_size: 12 }
        : { state: value, detailed: "verified", page_size: 12 };
    fetchInstitutions(filters)
      .then((r) => setTop(r.results.filter((x) => x.slug)))
      .catch(() => {});
  }, [mode, value]);

  if (!meta) return null;

  const siblings =
    mode === "category"
      ? (meta.categories || []).filter((c) => c !== value)
      : (meta.states || []).filter((s) => s !== value);

  return (
    <section
      className="border-t-2 border-[#0A0A0A] bg-white"
      data-testid="landing-related"
      aria-labelledby="landing-related-heading"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12 py-16 space-y-14">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#0A0A0A]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#4B5563]">
              Explore more
            </span>
          </div>
          <h2
            id="landing-related-heading"
            className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-2"
          >
            Keep browsing the directory
          </h2>
          <p className="text-[#4B5563] max-w-2xl">
            Related {mode === "category" ? "categories" : "states"} and frequently searched
            verified institutions inside {value}.
          </p>
        </div>

        {/* Top verified institutions in current scope */}
        {top.length > 0 && (
          <div>
            <div className="flex items-baseline justify-between border-b border-[#0A0A0A] pb-2 mb-5">
              <h3 className="font-display font-bold text-xl tracking-tight">
                Most searched in {value}
              </h3>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
                Verified entries
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {top.slice(0, 8).map((it) => (
                <Link
                  key={it.id}
                  to={`/${it.type === "Bank" ? "bank" : "nbfc"}/${it.slug}`}
                  className="group block border border-[#E5E7EB] hover:border-[#0A0A0A] p-4 transition-colors"
                  data-testid={`top-${it.slug}`}
                >
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4B5563] mb-1">
                    {it.category}
                  </div>
                  <div className="font-display font-bold text-sm leading-tight group-hover:text-[#002FA7]">
                    {it.name}
                  </div>
                  <div className="mt-1 font-mono text-[10px] text-[#4B5563]">
                    {it.hq_city}, {it.hq_state}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Sibling categories / states */}
        <div>
          <div className="flex items-baseline justify-between border-b border-[#0A0A0A] pb-2 mb-5">
            <h3 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
              {mode === "category" ? (
                <><Layers className="w-5 h-5" /> Other categories</>
              ) : (
                <><MapPin className="w-5 h-5" /> Other states</>
              )}
            </h3>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4B5563]">
              {siblings.length} options
            </span>
          </div>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6">
            {siblings.map((s) => (
              <li key={s} className="border-b border-[#E5E7EB] last:border-b-0">
                <Link
                  to={`/${mode}/${slugify(s)}`}
                  data-testid={`sibling-${slugify(s)}`}
                  className="flex items-center justify-between py-2.5 text-sm hover:text-[#002FA7] transition-colors"
                >
                  <span className="font-medium">{s}</span>
                  <ArrowUpRight className="w-3.5 h-3.5 text-[#4B5563]" />
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Cross-dimension block: on category page show popular states; on state page show categories */}
        {meta && (
          <div>
            <div className="flex items-baseline justify-between border-b border-[#0A0A0A] pb-2 mb-5">
              <h3 className="font-display font-bold text-xl tracking-tight flex items-center gap-2">
                {mode === "category" ? (
                  <><MapPin className="w-5 h-5" /> Browse by state</>
                ) : (
                  <><Layers className="w-5 h-5" /> Browse by category</>
                )}
              </h3>
            </div>
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6">
              {((mode === "category" ? meta.states : meta.categories) || []).map((s) => (
                <li key={s} className="border-b border-[#E5E7EB] last:border-b-0">
                  <Link
                    to={`/${mode === "category" ? "state" : "category"}/${slugify(s)}`}
                    className="flex items-center justify-between py-2.5 text-sm hover:text-[#002FA7] transition-colors"
                  >
                    <span className="font-medium">{s}</span>
                    <ArrowUpRight className="w-3.5 h-3.5 text-[#4B5563]" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* FAQ section for keyword coverage */}
        <div>
          <h3 className="font-display font-bold text-xl tracking-tight mb-4 border-b border-[#0A0A0A] pb-2">
            Frequently asked
          </h3>
          <dl className="space-y-4 text-sm text-[#4B5563] max-w-3xl">
            <div>
              <dt className="font-bold text-[#0A0A0A]">
                {mode === "category"
                  ? `How many ${value} institutions are registered with RBI?`
                  : `How many banks & NBFCs are headquartered in ${value}?`}
              </dt>
              <dd className="mt-1">
                The Reserve Bank of India publishes a master list of all {mode === "category" ? `${value} entities` : "regulated financial entities"}. BankDesks.com indexes every one of them and attempts to enrich them with Principal Nodal, Grievance, Recovery and Legal Head contacts.
              </dd>
            </div>
            <div>
              <dt className="font-bold text-[#0A0A0A]">
                How do I file a complaint against {mode === "category" ? `a ${value}` : `a bank or NBFC in ${value}`}?
              </dt>
              <dd className="mt-1">
                Start with the Grievance Redressal Officer listed on each institution's page. If unresolved within 30 days, escalate to the <a href="https://cms.rbi.org.in" className="text-[#002FA7] underline">RBI Integrated Ombudsman</a>.
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </section>
  );
};
